import type { TutorialStep } from "./types.ts";

/**
 * 「リマインダーの作成方法と自動設定」チュートリアルのステップ定義。
 *
 * 大きく 2 部構成:
 *   第1部  タスクの詳細パネルの「リマインダー」から、日時を指定して
 *          手動でリマインダーを追加する方法を学ぶ。
 *   第2部  全体設定の「自動リマインダー」タブから、プロジェクト単位で
 *          期限付きタスクへ自動でリマインダーを付ける設定と、その挙動を学ぶ。
 *          最後に、詳細パネルのリマインダーへ「自動」で付いたことを確認する。
 *
 * このチュートリアルでは練習用に、期限付きのタスクを 1 件用意済み。
 * targetKey は SandboxScreen 側の data-tut 属性と対応する。
 */
export const REMINDER_TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: "welcome",
    targetKey: null,
    placement: "center",
    title: "リマインダーを学ぶ",
    body:
      "指定した日時に Chatis へ通知を送る「リマインダー」を体験します。\n" +
      "リマインダーには2通りの付け方があります。まずはタスクごとに手動で設定する方法、次に、条件に合うタスクへ自動でリマインダーを付ける「自動リマインダー」を順番に進めます。\n" +
      "練習用に、期限のあるタスクを1件用意してあります。",
  },

  // ===== 第1部: 手動でリマインダーを設定する =====
  {
    id: "select-task",
    targetKey: "task-row",
    placement: "bottom",
    title: "① タスクの詳細を開く",
    body: "リマインダーを付けたいタスクの行をクリックして、右側の詳細パネルを開きましょう。",
    expectAction: "select-task",
  },
  {
    id: "open-reminder",
    targetKey: "detail-reminder-btn",
    placement: "top",
    title: "② 「リマインダー」を開く",
    body:
      "詳細パネルの下部にある「リマインダー」ボタンを押してください。\n" +
      "リマインダーの一覧と、新しく追加するための入力欄が表示されます。",
    expectAction: "reminder-open-dialog",
  },
  {
    id: "add-reminder",
    targetKey: "reminder-add-row",
    placement: "top",
    title: "③ 日時を指定して追加する",
    body:
      "通知したい「日付」と「時刻」を選び、「追加」を押しましょう。\n" +
      "指定した日時になると、このタスクの内容が Chatis に通知されます。",
    expectAction: "reminder-add",
  },
  {
    id: "confirm-reminder",
    targetKey: "reminder-list",
    placement: "bottom",
    title: "リマインダーが追加されました",
    body:
      "追加したリマインダーが一覧に表示されました。1つのタスクに複数のリマインダーを設定することもできます。\n" +
      "不要になったら、ゴミ箱アイコンから個別に削除できます。確認できたら「次へ」を押してください。",
  },
  {
    id: "close-reminder",
    targetKey: "reminder-close",
    placement: "left",
    title: "④ 閉じる",
    body: "「×」を押してリマインダーを閉じ、次は自動リマインダーの設定に進みましょう。",
    expectAction: "reminder-close",
  },

  // ===== 第2部: 自動リマインダーを設定する =====
  {
    id: "open-settings",
    targetKey: "settings-button",
    placement: "bottom",
    title: "⑤ 全体設定を開く",
    body:
      "自動リマインダーは「全体設定」から設定します。\n" +
      "画面右上の歯車「全体設定」ボタンを押してください。",
    expectAction: "open-settings",
  },
  {
    id: "open-auto-reminder-tab",
    targetKey: "settings-tab-autoReminder",
    placement: "bottom",
    title: "⑥ 「自動リマインダー」タブを開く",
    body: "上部のタブから「自動リマインダー」を選んでください。",
    expectAction: "open-auto-reminder-tab",
  },
  {
    id: "explain-auto",
    targetKey: "auto-reminder-info",
    placement: "bottom",
    title: "自動リマインダーとは",
    body:
      "プロジェクトごとに、期限のあるタスクへリマインダーを自動で付ける機能です。\n" +
      "対象にする「優先度」「ステータス」「タグ」を決めておくと、条件に合うタスクへ自動でリマインダーが設定されます。\n" +
      "1つのプロジェクトに複数のルールを作れるので、条件ごとに通知タイミングを変えることもできます。確認できたら「次へ」を押してください。",
  },
  {
    id: "toggle-auto",
    targetKey: "auto-reminder-toggle",
    placement: "right",
    title: "⑦ 自動リマインダーをONにする",
    body: "スイッチを押して、このプロジェクトの自動リマインダーをONにしましょう。",
    expectAction: "toggle-auto-reminder",
  },
  {
    id: "explain-form",
    targetKey: "auto-reminder-form",
    placement: "top",
    title: "通知のタイミングと対象を決める",
    body:
      "「期限の何日前・何時に通知するか」を指定します。\n" +
      "さらに、対象にする「優先度」「ステータス」「タグ」を選べます。タグは指定したタグをすべて持つタスクだけが対象になり（未指定なら条件なし）、完了・非表示のタスクには通知されません。\n" +
      "「保存時に既存タスクにも適用する」にチェックを入れると、今あるタスクにもすぐ反映されます。確認できたら「次へ」を押してください。",
  },
  {
    id: "save-auto",
    targetKey: "auto-reminder-save",
    placement: "top",
    title: "⑧ 保存する",
    body: "設定できたら「保存」を押しましょう。",
    expectAction: "save-auto-reminder",
  },
  {
    id: "explain-behavior",
    targetKey: null,
    placement: "center",
    title: "条件に合うタスクへ自動で付きます",
    body:
      "保存すると、条件（期限あり・対象の優先度・ステータス・タグ）に合うタスクへ、自動でリマインダーが設定されます。\n" +
      "タスクの期限・優先度・ステータス・タグを変更すると、リマインダーも自動で付け直されます。完了・非表示にすると通知されなくなります。\n" +
      "次で、先ほどのタスクに自動リマインダーが付いたか確認してみましょう。",
  },
  {
    id: "close-settings",
    targetKey: "settings-close",
    placement: "left",
    title: "⑨ 設定を閉じて確認する",
    body: "「×」を押して全体設定を閉じ、タスク側で反映を確認しましょう。",
    expectAction: "close-settings",
  },
  {
    id: "reopen-reminder",
    targetKey: "detail-reminder-btn",
    placement: "top",
    title: "⑩ もう一度リマインダーを開く",
    body: "詳細パネルの「リマインダー」ボタンをもう一度押してください。",
    expectAction: "reminder-open-dialog",
  },
  {
    id: "confirm-auto-badge",
    targetKey: "reminder-list",
    placement: "bottom",
    title: "「自動」リマインダーが付きました",
    body:
      "自動リマインダー設定によって付いたものには「自動」バッジが表示されます。\n" +
      "手動で追加したリマインダーと同じ一覧に並び、こちらも個別に削除できます。確認できたら「次へ」を押してください。",
  },
  {
    id: "done",
    targetKey: null,
    placement: "center",
    title: "お疲れさまでした！",
    body:
      "リマインダーは、タスク詳細の「リマインダー」から日時を指定して手動で設定できます。\n" +
      "また「全体設定 → 自動リマインダー」で、プロジェクトごとに条件を決めておくと、期限付きタスクへ自動でリマインダーが付きます。\n" +
      "「完了」を押すとチュートリアルを終了します。",
  },
];
